import { flexRender } from '@tanstack/react-table';
import { BasicTable } from './BasicTable';
import { type ReactTableProps, isColumnMetaWithSelectFilterVariant } from './types';

/*
 * This component assumes you're passing a table object from the useReactTable hook imported from @tanstack/react-table
 * Passing in paginationText is necessary for localization
 */
export const ReactTable = <T,>(props: ReactTableProps<T>) => {
  const { table, paginationText, tableFilterLocalizations } = props;

  const headers = table.getHeaderGroups().flatMap((headerGroup) =>
    headerGroup.headers.map((header) => ({
      id: header.id,
      content: flexRender(header.column.columnDef.header, header.getContext()),
      isSorted: header.column.getIsSorted(),
      onClick: header.column.getToggleSortingHandler(),
      width: header.column.columnDef.size,
      filterValue: header.column.getFilterValue(),
      setFilterValue: header.column.setFilterValue,
      meta: isColumnMetaWithSelectFilterVariant(header.column.columnDef.meta)
        ? header.column.columnDef.meta
        : undefined,
    })),
  );

  const sortedColumn = headers.find((header) => header.isSorted);

  const rows = table.getRowModel().rows.map((row) => ({
    id: row.id,
    cells: row.getVisibleCells().map((cell) => ({
      id: cell.id,
      content: flexRender(cell.column.columnDef.cell, cell.getContext()),
      active: sortedColumn ? cell.column.id === sortedColumn.id : false,
    })),
  }));

  const paginationEnabled = table.getState().pagination !== undefined;

  const pagination = paginationEnabled
    ? {
        handleNextPage: () => table.nextPage(),
        handlePreviousPage: () => table.previousPage(),
        handleFirstPage: () => table.firstPage(),
        handleLastPage: () => table.lastPage(),
        canGoToPreviousPage: table.getCanPreviousPage(),
        canGoToNextPage: table.getCanNextPage(),
        paginationText:
          paginationText ??
          `${table.getState().pagination.pageIndex + 1} of ${table
            .getPageCount()
            .toLocaleString()}`,
      }
    : undefined;

  return (
    <BasicTable
      headers={headers}
      rows={rows}
      pagination={pagination}
      tableFilterLocalizations={tableFilterLocalizations}
    />
  );
};
